import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { type BemanningUser } from '../hooks/useDraftBemanningDrawer';
import { getErfaringsFarge, getErfaringsLabel } from '../utils/userUtils';

interface UserCardProps {
  user: BemanningUser;
  selectedTalentName: string | null;
  isSelected: boolean;
  isAllokert: boolean;
  isInDraft: boolean;
  isRemovedFromDraft: boolean;
  onToggle: (userId: number, event?: React.MouseEvent) => void;
}

export const UserCard: React.FC<UserCardProps> = ({
  user,
  selectedTalentName,
  isSelected,
  isAllokert,
  isInDraft,
  isRemovedFromDraft,
  onToggle,
}) => {
  const userTalent = user.talents.find((t: { talent_navn: string }) => t.talent_navn === selectedTalentName);

  return (
    <div
      key={`user-${user.id}`}
      onClick={() => { if (!isInDraft) onToggle(user.id); }}
      className={cn(
        'mb-2 p-3 rounded-lg border transition-all cursor-pointer',
        isSelected ? 'border-primary bg-primary/10' : 'border-border bg-card',
        isAllokert && 'bg-muted',
        isRemovedFromDraft && 'opacity-50',
        'hover:shadow-md'
      )}
    >
      <div className="flex items-center gap-3">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggle(user.id)}
          onClick={(e) => e.stopPropagation()}
          disabled={isInDraft}
        />
        <Avatar className="h-8 w-8">
          <AvatarFallback className="text-xs">
            {user.first_name.charAt(0)}{user.last_name.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium truncate">
              {user.first_name} {user.last_name}
            </p>
            {isInDraft && (
              <Badge className="bg-green-100 text-green-800 text-xs">Ny</Badge>
            )}
            {isRemovedFromDraft && (
              <Badge variant="destructive" className="text-xs">Fjernet</Badge>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {userTalent && (
              <Badge className={cn('text-xs px-1.5 py-0', getErfaringsFarge(userTalent.erfaringsnivaa))}>
                {getErfaringsLabel(userTalent.erfaringsnivaa)}
              </Badge>
            )}
            {userTalent?.sertifisert && (
              <CheckCircle className="h-3.5 w-3.5 text-green-600" />
            )}
            {user.allokertTalenter && user.allokertTalenter.map((talentNavn: string, idx: number) => (
              <Badge 
                key={idx}
                className="text-xs bg-status-waiting/10 text-amber-800 border-amber-300"
              >
                {talentNavn}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

